<template>
  <AppDialog
    v-model="isVisible"
    modal
    :closable="true"
    custom-class="expired-chat-dialog backdrop-blur-xl! relative px-4"
    width="min(1200px, calc(100vw - 2rem))"
    :breakpoints="{ '640px': 'calc(100vw - 1rem)' }"
  >
    <template #header>
      <div
        class="mt-5 flex w-full flex-col gap-3 pr-8 pb-2 sm:flex-row sm:items-start sm:gap-4 sm:pr-10"
      >
        <div class="flex w-full min-w-0 flex-col gap-3 sm:gap-4">
          <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
            <div class="flex min-w-0 flex-col">
              <div class="flex flex-row gap-2">
                <div class="flex items-center w-fit shrink-0 rounded-md bg-[var(--p-primary-color)]/20 p-1.5">
                  <IconMessageInfo width="30" height="30" class="text-[var(--p-primary-color)]" />
                </div>
                <div class="flex flex-col">
                  <label
                    class="montserrat break-words text-xl font-semibold leading-tight text-[var(--p-primary-color)] sm:text-2xl"
                  >
                    {{ $t('whatsapp.reactivate_conversation') }}
                  </label>
                  <label
                    class="mt-0 text-xs leading-snug text-[var(--p-surface-600)] dark:text-[var(--p-surface-100)]"
                  >
                    {{ $t('whatsapp.reactivate_conversation_hint') }}
                  </label>
                </div>
              </div>
            </div>

            <div
              class="flex min-w-0 flex-1 items-center justify-start gap-1 sm:flex-none sm:justify-end gap-5"
            >
              <!-- Seccion de plantilla -->
              <div class="flex min-w-0 items-center justify-center gap-1 sm:justify-start sm:gap-2">
                <div
                  class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--p-primary-color)] text-sm font-semibold text-white"
                >
                  1
                </div>
                <label
                  class="montserrat sr-only truncate text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-text-color)] sm:not-sr-only lg:block"
                >
                  {{ $t('whatsapp.select_template') }}
                </label>
              </div>
              <!-- Seccion de resumen -->
              <div class="flex min-w-0 items-center justify-center gap-1 sm:justify-start sm:gap-2">
                <div
                  class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
                  :class="
                    isSummaryStepActive
                      ? 'bg-[var(--p-primary-color)] '
                      : 'bg-[var(--p-surface-400)] dark:bg-[var(--p-surface-600)]'
                  "
                >
                  2
                </div>
                <label
                  class="montserrat sr-only truncate text-sm font-semibold sm:not-sr-only lg:block"
                  :class="
                    isSummaryStepActive
                      ? 'text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]'
                      : 'text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]'
                  "
                >
                  {{ $t('whatsapp.send_details') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="min-h-[400px] dark:text-[var(--p-text-color)]">
      <!-- <SelectCampaignContactsStep
        v-if="currentStep === 1"
        ref="campaignContactsStepRef"
        :campaigns="campaigns"
        :is-loading-campaigns="isLoadingCampaigns"
        @update:contacts="handleContactsUpdate"
        @update:campaign="handleCampaignUpdate"
        @load-contacts="handleLoadContacts"
      /> -->

      <KeepAlive :max="1">
        <SelectTemplateStep
          v-if="currentStep === 1"
          :key="`template-${dialogSessionKey}`"
          :templates="availableTemplates"
          :campaign-id="selectedCampaignForFilter?.id"
          :is-loading-templates="isLoadingTemplates"
          :templates-meta="templateService.templatesMeta.value"
          :selected-template="selectedTemplate"
          @update:template="handleTemplateUpdate"
          @update:parameters="handleParametersUpdate"
          @load-templates="loadTemplatesForSelectedCampaign"
        />
      </KeepAlive>

      <!-- <SelectRoutingDestinationStep
        v-else-if="currentStep === 2"
        :campaigns="campaignsForRouting"
        :users="users"
        :bots="bots"
        :is-loading-campaigns="isLoadingCampaignsForRouting"
        :is-loading-users="isLoadingUsers"
        :is-loading-bots="isLoadingBots"
        @update:destination="handleDestinationUpdate"
        @load-campaigns="handleLoadCampaignsForRouting"
        @load-users="handleLoadUsers"
        @load-bots="handleLoadBots"
      /> -->

      <PreviewAndSendStep
        v-if="currentStep === 2"
        :contacts="selectedContacts"
        :template="selectedTemplate"
        :parameters="templateParameters"
        :destination="routingDestination"
      />
    </div>

    <div class="mt-4">
      <div class="flex w-full justify-end gap-2 sm:gap-4">
        <!-- Atrás -->
        <AppButtonIcon
          v-if="currentStep > 1"
          :label="$t('general.back')"
          severity="secondary"
          size="small"
          class="montserrat w-full whitespace-nowrap sm:w-1/4"
          :disabled="isLoading"
          @click="handlePreviousStep"
        >
          <template #icon>
            <ChevronLeft :size="20" />
          </template>
        </AppButtonIcon>

        <!-- Spacer cuando no hay botón izquierdo -->
        <div v-else class="hidden sm:block sm:flex-1" />

        <!-- Siguiente -->
        <AppButtonIcon
          v-if="currentStep < 2"
          :label="$t('whatsapp.send_template_dialog.next')"
          severity="primary"
          icon-pos="right"
          size="small"
          class="montserrat w-full whitespace-nowrap sm:w-1/4"
          :disabled="!canProceedToNextStep"
          @click="handleNextStep"
        >
          <template #icon>
            <ChevronRight :size="20" />
          </template>
        </AppButtonIcon>

        <!-- Enviar -->
        <AppButtonIcon
          v-else
          :label="$t('whatsapp.send_template_dialog.send_template')"
          size="small"
          icon-pos="right"
          class="w-full whitespace-nowrap sm:w-1/4"
          :loading="isLoading"
          :disabled="!canSendTemplate"
          @click="handleSend"
        >
          <template #icon>
            <SendHorizontal :size="16" />
          </template>
        </AppButtonIcon>
      </div>
    </div>
  </AppDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, KeepAlive } from 'vue'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import { ChevronLeft, ChevronRight, SendHorizontal } from 'lucide-vue-next'
import SelectCampaignContactsStep from './steps/SelectCampaignContactsStep.vue'
import SelectTemplateStep from './steps/SelectTemplateStep.vue'
import PreviewAndSendStep from './steps/PreviewAndSendStep.vue'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useTemplateService } from '@/components/whatsapp/services/useTemplateService'
import { useBotService } from '@/services/bot/useBotService'
import { useContactService } from '@/services/contact/useContactService'
import IconMessageInfo from '@/assets/svg/message_info.svg?component'
import type {
  ContactSelection,
  Template,
  RoutingDestination,
  TemplateSendComponent,
} from '@/components/whatsapp/types/interfaces'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { BotOption } from '@/services/bot/interfaces'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'
import { useI18n } from 'vue-i18n'

interface UserOption {
  id: string
  name: string
  email?: string
}

interface Props {
  visible: boolean
  initialContact?: ContactSelection
  initialCampaignId?: string
  incomingRouteId?: string
  chatId?: string
}

const props = defineProps<Props>()
const { t } = useI18n()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  send: [
    data: {
      contacts: ContactSelection[]
      template: Template
      parameters: TemplateSendComponent[] | null
      destination: RoutingDestination
      incoming_route_id?: string
      campaign_id?: string
      chat_id?: string
      is_bulk: boolean
    },
  ]
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

const currentStep = ref(1)
const isLoading = ref(false)
const dialogSessionKey = ref(0)
const isSummaryStepActive = computed(() => currentStep.value >= 2)

const selectedContacts = ref<ContactSelection[]>([])
const selectedCampaignForFilter = ref<IOptionsCampaign | null>(null)
const selectedTemplate = ref<Template | null>(null)
const templateParameters = ref<TemplateSendComponent[] | null>(null)
const routingDestination = ref<RoutingDestination | null>(null)

const campaigns = ref<IOptionsCampaign[]>([])
const campaignsForRouting = ref<IOptionsCampaign[]>([])
const users = ref<UserOption[]>([])
const bots = ref<BotOption[]>([])

const isLoadingCampaigns = ref(false)
const isLoadingTemplates = ref(false)
const isLoadingCampaignsForRouting = ref(false)
const isLoadingUsers = ref(false)
const isLoadingBots = ref(false)

const campaignContactsStepRef = ref<InstanceType<typeof SelectCampaignContactsStep> | null>(null)

const campaignService = useCampaignService()
const templateService = useTemplateService()
const botService = useBotService()
const contactService = useContactService()

const isSimplifiedFlow = computed(() => !!props.initialContact)

const stepTitle = computed(() => {
  if (isSimplifiedFlow.value) {
    switch (currentStep.value) {
      case 1:
        return t('whatsapp.expired_chat_dialog.reactivate_conversation')
      case 2:
        return t('whatsapp.expired_chat_dialog.confirm_and_send')
      default:
        return t('whatsapp.send_template_dialog.send_template')
    }
  }

  switch (currentStep.value) {
    // case 1:
    //   return 'Paso 1: Seleccionar Campaña y Contactos'
    case 1:
      return t('whatsapp.expired_chat_dialog.step_select_template')
    // case 2:
    //   return 'Paso 2: Configurar Destino de Respuestas'
    case 2:
      return t('whatsapp.expired_chat_dialog.step_confirm_and_send')
    default:
      return t('whatsapp.send_template_dialog.send_template')
  }
})

const availableTemplates = computed(() => {
  return templateService.templates.value || []
})

const hasCompletedTemplateSelection = computed(() => {
  return selectedTemplate.value !== null
})

const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 1:
      return hasCompletedTemplateSelection.value
    case 2:
      return hasCompletedTemplateSelection.value
    case 3:
      return routingDestination.value !== null
    default:
      return false
  }
})

const canSendTemplate = computed(() => {
  return (
    selectedContacts.value.length > 0 &&
    selectedTemplate.value !== null &&
    routingDestination.value !== null
  )
})

const handleContactsUpdate = (contacts: ContactSelection[]) => {
  selectedContacts.value = contacts
}

const handleCampaignUpdate = (campaign: IOptionsCampaign | null) => {
  selectedCampaignForFilter.value = campaign
  selectedTemplate.value = null
  templateParameters.value = null
  templateService.templates.value = []
}

const handleTemplateUpdate = (template: Template | null) => {
  selectedTemplate.value = template
}

const handleParametersUpdate = (parameters: TemplateSendComponent[] | null) => {
  templateParameters.value = parameters
}

const handleDestinationUpdate = (destination: RoutingDestination | null) => {
  routingDestination.value = destination
}

const handleLoadContacts = async (campaignId: string) => {
  try {
    const response = await contactService.getContacts({ campaign_id: campaignId })
    // Un contacto registrado desde WhatsApp puede no tener teléfono: se le
    // envía por su BSUID.
    const contactOptions = response.data
      .filter((contact) => contact.phone || contact.bsuid)
      .map((contact) => ({
        id: contact.id,
        phone: contact.phone ?? '',
        bsuid: contact.bsuid ?? null,
        name: contact.name,
        label: contact.phone ? `${contact.name} (${contact.phone})` : contact.name,
      }))

    campaignContactsStepRef.value?.setAvailableContacts(contactOptions)
  } catch (error) {}
}

const handleLoadCampaignsForRouting = async () => {
  isLoadingCampaignsForRouting.value = true
  try {
    campaignsForRouting.value = await campaignService.getAllCampaigns({
      campaign_type: TypeCampaign.WHATSAPP,
    })
    users.value = []
  } catch (error) {
  } finally {
    isLoadingCampaignsForRouting.value = false
  }
}

const handleLoadUsers = async (campaignId: string) => {
  isLoadingUsers.value = true
  try {
    users.value = await campaignService.getCampaignUsers(campaignId)
  } catch (error) {
    users.value = []
  } finally {
    isLoadingUsers.value = false
  }
}

const handleLoadBots = async () => {
  isLoadingBots.value = true
  try {
    bots.value = await botService.getBotOptions()
  } catch (error) {
  } finally {
    isLoadingBots.value = false
  }
}

const loadTemplatesForSelectedCampaign = async (params?: {
  search?: string | null
  category?: string | null
  page?: number
  size?: number
}) => {
  if (!selectedCampaignForFilter.value) {
    templateService.templates.value = []
    return
  }

  isLoadingTemplates.value = true

  try {
    await templateService.fetchTemplatesByCampaign(selectedCampaignForFilter.value.id, {
      search: params?.search,
      category: params?.category,
      page: params?.page ?? 1,
      size: params?.size ?? 10,
      force: true,
    })
  } catch (error) {
  } finally {
    isLoadingTemplates.value = false
  }
}

const handleNextStep = () => {
  if (!canProceedToNextStep.value) return

  if (isSimplifiedFlow.value && currentStep.value === 2) {
    currentStep.value = 2
  } else if (currentStep.value < 2) {
    currentStep.value++
  }
}

const handlePreviousStep = () => {
  if (isSimplifiedFlow.value && currentStep.value === 4) {
    currentStep.value = 2
  } else if (currentStep.value > 1) {
    currentStep.value--
  }
}

const handleSend = async () => {
  if (!canSendTemplate.value) return

  isLoading.value = true

  try {
    const sendData = {
      contacts: selectedContacts.value,
      template: selectedTemplate.value!,
      parameters: templateParameters.value,
      destination: routingDestination.value!,
      incoming_route_id: props.incomingRouteId,
      campaign_id: selectedCampaignForFilter.value?.id,
      is_bulk: false,
      ...(props.chatId && { chat_id: props.chatId }),
    }
    emit('send', sendData)
  } catch (error) {
  } finally {
    isLoading.value = false
    handleClose()
  }
}

const handleClose = () => {
  if (isLoading.value) return

  currentStep.value = 1
  dialogSessionKey.value += 1
  selectedContacts.value = []
  selectedCampaignForFilter.value = null
  selectedTemplate.value = null
  templateParameters.value = null
  routingDestination.value = null

  emit('update:visible', false)
}

const loadInitialData = async () => {
  isLoadingCampaigns.value = true

  try {
    campaigns.value = await campaignService.getAllCampaigns()
  } catch (error) {
  } finally {
    isLoadingCampaigns.value = false
  }
}

watch(
  () => props.visible,
  async (visible) => {
    if (visible) {
      await loadInitialData()

      if (props.initialContact) {
        selectedContacts.value = [props.initialContact]
        currentStep.value = 1
      }

      if (props.initialCampaignId) {
        const campaign = campaigns.value.find((c) => c.id === props.initialCampaignId)
        if (campaign) {
          selectedCampaignForFilter.value = campaign

          routingDestination.value = {
            type: 'campaign',
            campaign_id: campaign.id,
            campaign_name: campaign.name,
          }
        }
      }

      if (selectedCampaignForFilter.value) {
        await loadTemplatesForSelectedCampaign()
      }
    } else {
      handleClose()
    }
  },
)
</script>
